import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { FloatingWidgets } from './FloatingWidgets';
import ChatBot from './ChatBot';
import { CheckCircle, Clock, Shield, Phone, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

/**
 * Template réutilisable pour les Pages Piliers SEO
 * Usage : <ServicePage config={PAGE_CONFIG} />
 */
export const ServicePage = ({ config }) => {
  const [openFaq, setOpenFaq] = useState(null);

  // Mise à jour dynamique du title + meta description + schemas JSON-LD pour le SEO
  useEffect(() => {
    document.title = config.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', config.metaDescription);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://phonelab69.fr${window.location.pathname}`);
    window.scrollTo(0, 0);

    const pageUrl = `https://phonelab69.fr${window.location.pathname}`;
    const pageName = config.h1.replace('\n', ' ');

    // FAQ Schema
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'sp-faq-schema';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faq.map(q => ({
        '@type': 'Question',
        name: q.q,
        acceptedAnswer: { '@type': 'Answer', text: q.a }
      }))
    });
    document.head.appendChild(faqScript);

    // BreadcrumbList Schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'sp-breadcrumb-schema';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://phonelab69.fr/' },
        { '@type': 'ListItem', position: 2, name: pageName, item: pageUrl }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    // Service Schema
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.id = 'sp-service-schema';
    serviceScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: pageName,
      description: config.subtitle,
      url: pageUrl,
      provider: {
        '@type': 'LocalBusiness',
        '@id': 'https://phonelab69.fr',
        name: 'PHONELAB 69 VILLEURBANNE',
        telephone: '+33426008210',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '6 Rue Paul Verlaine',
          addressLocality: 'Villeurbanne',
          postalCode: '69100',
          addressCountry: 'FR'
        }
      },
      areaServed: [
        { '@type': 'City', name: 'Villeurbanne' },
        { '@type': 'City', name: 'Lyon' },
        { '@type': 'AdministrativeArea', name: 'Lyon Métropole' }
      ],
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'EUR',
          description: 'Tarif variable selon modèle et qualité de pièce. Devis gratuit.'
        }
      }
    });
    document.head.appendChild(serviceScript);

    return () => {
      document.querySelector('#sp-faq-schema')?.remove();
      document.querySelector('#sp-breadcrumb-schema')?.remove();
      document.querySelector('#sp-service-schema')?.remove();
      document.title = 'PHONELAB 69 – Réparation iPhone & Samsung Villeurbanne / Lyon | Express 15min';
    };
  }, [config]);

  const scrollToQuote = () => {
    const el = document.getElementById('contact-cta');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Navbar />

      {/* ── Hero Section ──────────────────────────────────────── */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center py-16">
          {/* Breadcrumb */}
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white/80 text-sm mb-6 transition-colors">
            <ArrowLeft size={14} />
            Accueil
          </Link>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <span className="text-lg">{config.icon}</span>
            <span className="text-white/80 text-sm font-medium">{config.badge}</span>
          </div>
          {/* H1 */}
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight">
            {config.h1}
          </h1>
          <p className="text-xl text-white/65 mb-8 max-w-2xl mx-auto leading-relaxed">
            {config.subtitle}
          </p>
          {/* Stats rapides */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {config.stats.map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 rounded-xl px-4 py-2">
                <span className="text-lg">{s.icon}</span>
                <span className="text-white font-semibold text-sm">{s.label}</span>
              </div>
            ))}
          </div>
          {/* CTA hero */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={scrollToQuote}
              className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-blue-600/30">
              Devis gratuit
            </button>
            <a href="tel:+33426008210"
              className="flex items-center justify-center gap-2 border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-lg transition-all">
              <Phone size={18} />
              04 26 00 82 10
            </a>
          </div>
        </div>
      </section>

      {/* ── Services couverts ────────────────────────────────── */}
      <section className="relative py-16 bg-black/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">
            {config.servicesTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.services.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/8 transition-colors">
                <div className="text-2xl flex-shrink-0">{s.icon}</div>
                <div>
                  <p className="font-semibold text-white text-sm">{s.name}</p>
                  {s.models && <p className="text-xs text-white/45 mt-0.5">{s.models}</p>}
                  {s.duration && (
                    <div className="flex items-center gap-1 mt-1">
                      <Clock size={11} className="text-blue-400" />
                      <span className="text-xs text-blue-400">{s.duration}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Garanties + Réassurance ──────────────────────────── */}
      <section className="relative py-12 bg-black/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-4 text-center">
            <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-5">
              <Shield className="text-emerald-400 mx-auto mb-2" size={24} />
              <p className="font-bold text-white">{config.guarantee.duration}</p>
              <p className="text-xs text-white/50 mt-1">{config.guarantee.label}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl p-5">
              <Clock className="text-blue-400 mx-auto mb-2" size={24} />
              <p className="font-bold text-white">{config.guarantee.speed}</p>
              <p className="text-xs text-white/50 mt-1">Réparation express</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-5">
              <CheckCircle className="text-amber-400 mx-auto mb-2" size={24} />
              <p className="font-bold text-white">Diagnostic GRATUIT</p>
              <p className="text-xs text-white/50 mt-1">Sans engagement</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section className="relative py-16 bg-black/40 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {config.faq.map((q, i) => (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors"
                  data-testid={`faq-q-${i}`}
                >
                  <span className="font-semibold text-white text-sm pr-4">{q.q}</span>
                  {openFaq === i
                    ? <ChevronUp size={16} className="text-white/50 flex-shrink-0" />
                    : <ChevronDown size={16} className="text-white/50 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 bg-white/3">
                    <p className="text-sm text-white/65 leading-relaxed">{q.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sections supplémentaires (ex: Maintenance) ─────── */}
      {config.extraSections && config.extraSections.map((section, si) => (
        <section key={si} id={section.id} className="relative py-16 bg-black/35 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{section.title}</h2>
              {section.subtitle && (
                <p className="text-white/55 max-w-2xl mx-auto text-base">{section.subtitle}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {section.items.map((item, ii) => (
                <div key={ii} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/8 transition-colors">
                  <span className="text-3xl flex-shrink-0">{item.icon}</span>
                  <div>
                    <p className="font-semibold text-white mb-1">{item.name}</p>
                    <p className="text-sm text-white/55 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* CTA inline après chaque section technique */}
            <div className="text-center mt-8">
              <button
                onClick={scrollToQuote}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-7 py-3 rounded-full font-semibold text-sm transition-all hover:scale-105"
              >
                Demander mon devis gratuit
              </button>
            </div>
          </div>
        </section>
      ))}

      {/* ── Zone géographique desservie ─────────────────────── */}
      <section className="relative py-10 bg-black/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-white/40 text-xs uppercase tracking-widest mb-4 font-semibold">Nous intervenons pour les résidents de</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              'Gratte-Ciel', 'Charpennes', 'Cusset', 'Tonkin', 'Saint-Jean', 'Buers',
              'Lyon 1er–8e', 'Caluire', 'Bron', 'Décines', 'Vénissieux', 'Meyzieu', 'Vaulx-en-Velin',
            ].map((z, i) => (
              <span key={i} className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-white/55">
                {z}
              </span>
            ))}
          </div>
          <p className="text-white/35 text-xs mt-4">
            📍 6 Rue Paul Verlaine, 69100 Villeurbanne — Tram T6, arrêt Gratte-Ciel · 5 min de la station Charpennes (Métro A)
          </p>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────── */}
      <section id="contact-cta" className="relative py-16 bg-blue-600/20 border-y border-blue-500/20 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{config.ctaTitle}</h2>
          <p className="text-white/60 mb-8">{config.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+33426008210"
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg">
              <Phone size={18} />
              Appeler maintenant
            </a>
            <Link to="/#quote"
              className="flex items-center justify-center border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-lg transition-all">
              Formulaire de devis
            </Link>
          </div>
          <p className="text-white/40 text-sm mt-5">
            📍 6 Rue Paul Verlaine, 69100 Villeurbanne — Tram T6, arrêt Gratte-Ciel
          </p>
        </div>
      </section>

      <Footer />
      <FloatingWidgets />
      <ChatBot />
    </>
  );
};
